'use strict';

/**
 * Helper module for initialization popover elements
 *
 * @module popover
 */

const { queryFirst, queryAll, addClass, removeClass } = require('./domUtil');

const phInfoPopover = queryFirst('.ph-number-info-popover .content-asset');
const fpoInfoPopover = queryFirst('.fpo-info-popover .content-asset');
const checkoutBenefitsInfoPopover = queryFirst('.checkout-benefits-info .checkout-benefits-info-content');
const cvvInfoPopover = queryFirst('.cvv-info-popover .cvv-info-popover-content');
const giftCardInfoPopover = queryFirst('.gift-card-number-info-popover .content-asset');
const deliveryFeeInfoPopover = queryFirst('.delivery-fee-info-popover .content-asset');
const boltSignoutMessage = queryFirst('.bolt-logout-button .bolt-sign-out');
const giftReturnNumberInfoPopover = queryFirst('.gift-return-order-no-popover .content-asset');
const earlyAccessLockedSelector = '.loyalty-early-access-locked.has-early-access-container';
const pdpChatWrapper = queryFirst('.pdp-chat');
const pdpChatPopover = queryFirst('.js-toggle-chat-popover');
const pdpChatContainer = queryFirst('.pdp-chat-container');
const keepUserSignedInPopoverCheckoutPage = queryFirst('.existing-customer-sheet .keep-user-signed-in');
const tierInfoPopover = queryFirst('.tier-info-popover .content-asset');
const { EVENT_KEY_ESC, HIDDEN_CLASS } = require('./constants');

// default popover options on hover
const POPOVER_HOVER_BOTTOM = {
    trigger: 'hover',
    placement: 'bottom',
    html: true
};

/**
 * Resolves the visible early-access / AMG tooltip node for a lock container.
 * @param {HTMLElement} lockContainer - Lock icon wrapper element
 * @returns {HTMLElement|null} Visible tooltip content node, if any
 */
const getEarlyAccessTooltipContent = lockContainer => {
    if (!lockContainer) return null;
    // Only the visible wrap for the current event — never fall back to the other event's copy.
    return queryFirst('.js-early-access-tooltip-wrap:not(.d-none) .early-access-container-tooltip', lockContainer);
};

/**
 * Popover content must be a clone/HTML string. Passing the live DOM node moves it
 * into the popover, so the next color-swatch switch finds an empty wrap.
 * @param {HTMLElement|null} tooltipEl - Tooltip source node
 * @returns {string} HTML for the popover body
 */
const getEarlyAccessTooltipHtml = tooltipEl => (tooltipEl ? tooltipEl.innerHTML : '');

/**
 * Shows EA or AMG tooltip markup inside a lock container and refreshes its popover.
 * @param {HTMLElement} lockContainer - Lock icon wrapper element
 * @param {boolean} isAnnualMemberGift - Whether to show Annual Member Gift copy
 */
const updateEarlyAccessLockTooltip = (lockContainer, isAnnualMemberGift) => {
    if (!lockContainer) return;

    const wraps = queryAll('.js-early-access-tooltip-wrap', lockContainer);
    if (!wraps.length) return;

    const wantAmg = !!isAnnualMemberGift;
    wraps.forEach(wrap => {
        const isAmgWrap = wrap.dataset.tooltipType === 'amg';
        const shouldShow = wantAmg ? isAmgWrap : !isAmgWrap;
        if (shouldShow) {
            removeClass(wrap, HIDDEN_CLASS);
        } else {
            addClass(wrap, HIDDEN_CLASS);
        }
    });

    // Do not fall back to the other event's copy — wrong SL/body is worse than no tooltip.
    lockContainer.dataset.isAnnualMemberGift = wantAmg ? 'true' : 'false';

    const button = queryFirst(earlyAccessLockedSelector, lockContainer);
    if (!button) return;

    const tooltip = getEarlyAccessTooltipContent(lockContainer);
    if (!tooltip) return;

    const $button = $(button);
    if ($button.data('bs.popover')) {
        $button.popover('dispose');
    }
    $button.attr('data-lp-popover-trigger', 'true').popover({
        trigger: 'hover click',
        placement: 'bottom',
        html: true,
        content: getEarlyAccessTooltipHtml(tooltip)
    });
};

const initPopover = (selector, container, ...options) => {
    const popoverOptions = Object.assign({}, POPOVER_HOVER_BOTTOM, ...options);
    const $elements = $(selector, container || document.body);

    $elements
        .attr('data-lp-popover-trigger', 'true')
        .popover(popoverOptions);
};

// default popover option on click
const POPOVER_CLICK_BOTTOM = {
    trigger: 'click',
    placement: 'bottom',
    html: true
};

/*
 * Handle clicks outside of popover in order to close it
 */
const initPopoverClose = container => {
    const $container = $(container || document.body);

    $container
        .find('[data-lp-popover-trigger="true"]')
        .off('shown.bs.popover.popoverClose')
        .on('shown.bs.popover.popoverClose', function () {
            const $trigger = $(this);

            const closePopover = () => {
                if (!$trigger.attr('aria-describedby')) return;
                $trigger.popover('hide');
            };

            const onDocClick = e => {
                const $target = $(e.target);
                const popoverId = $trigger.attr('aria-describedby');
                const $popover = popoverId ? $(`#${popoverId}`) : null;

                const clickOnTrigger = $target.closest($trigger).length > 0;
                const clickInsidePopover = $popover && $target.closest($popover).length > 0;

                if (clickOnTrigger || clickInsidePopover) {
                    return;
                }

                closePopover();
                $(document)
                    .off('click.popoverClose', onDocClick)
                    .off('keydown.popoverClose', onDocKeydown);
            };

            const onDocKeydown = e => {
                if (e.key !== EVENT_KEY_ESC) return;

                closePopover();
                $(document)
                    .off('click.popoverClose', onDocClick)
                    .off('keydown.popoverClose', onDocKeydown);
            };

            $(document)
                .on('click.popoverClose', onDocClick)
                .on('keydown.popoverClose', onDocKeydown);
        });
};

/*
 * Assign custom close event on custom selector
 */
const initPopoverCloseSelector = container => {
    $('[data-close-popover-selector]', container || document.body).on('click', function () {
        const tooltipSelector = $(this).data('close-popover-selector');
        $(tooltipSelector).popover('hide');
    });
};

/**
 * Initializing popover for promotions on a page
 */
const initPopoverPromotions = () => {
    queryAll('.promotions-info').forEach(button => {
        const popoverContent = queryFirst('.promotions-info-popover .promotions-info-popover-content', button.parentElement);

        $(button).popover({
            trigger: 'click',
            placement: 'bottom',
            html: true,
            content: popoverContent
        });
    });
};

/**
 * Initializing popover for strike hearts on a page
 */
const initPopoverWishList = () => {
    queryAll('.strike-wish-list').forEach(button => {
        const popoverContent = button.dataset.content;
        $(button).popover({
            placement: 'bottom',
            trigger: 'hover click',
            html: true,
            content: popoverContent
        });
    });
};

/**
 * Early access locked message popover — content is resolved per lock container
 * so EA and AMG tooltips can coexist on the same page.
 * @param {HTMLElement} [container] - Optional root to scope initialization
 */
const initPopoverEarlyAccessLocked = container => {
    const root = container || document.body;
    queryAll(earlyAccessLockedSelector, root).forEach(button => {
        const lockContainer = button.closest('.loyalty-early-access-lock-container');
        const tooltip = getEarlyAccessTooltipContent(lockContainer);
        if (!tooltip) return;

        const $button = $(button);
        if ($button.data('bs.popover')) {
            $button.popover('dispose');
        }
        $button.attr('data-lp-popover-trigger', 'true').popover({
            trigger: 'hover click',
            placement: 'bottom',
            html: true,
            content: getEarlyAccessTooltipHtml(tooltip)
        });
    });
};

const initPopoverAllLocal = container => {
    const birthdayInfoPopover = queryFirst('.birthday-info-popover .content-asset');
    const keepUserSignedInPopover = queryFirst('.remember-me .keep-user-signed-in');
    const keepUserSignedInContainer = queryFirst('.remember-me .logged-in-user-notification');

    initPopover('.ph-number-info', container, {
        content: phInfoPopover
    });

    const popoverIcons = queryAll('.loyalty-tooltip-icon');
    popoverIcons.forEach(tooltipIcon => {
        const popContent = queryFirst('.loyalty-tooltip-popover', tooltipIcon.parentElement);
        initPopover(tooltipIcon, container, {
            content: popContent,
            placement: 'bottom'
        });
    });

    initPopover('.birthday-info', container, {
        content: birthdayInfoPopover,
        customClass: 'birthday-popover'
    });

    initPopover('.fpo-info', container, {
        content: fpoInfoPopover
    });

    initPopover('.tier-status-info', container, {
        content: tierInfoPopover
    });

    initPopover('.checkout-benefits-info', container, {
        container: '.new-customers',
        offset: '160 0',
        content: checkoutBenefitsInfoPopover
    });

    initPopover('.cvv-info', container, {
        content: cvvInfoPopover
    });

    initPopover('.gift-return-order-no', container, POPOVER_CLICK_BOTTOM, {
        content: giftReturnNumberInfoPopover
    });

    initPopover('.gift-card-number-info', container, {
        content: giftCardInfoPopover
    });

    initPopover('.delivery-fee-info', container, {
        content: deliveryFeeInfoPopover
    });

    initPopover('.bolt-logout-button-container', container, POPOVER_CLICK_BOTTOM, {
        content: boltSignoutMessage
    });

    initPopoverPromotions();
    initPopoverWishList();

    initPopoverEarlyAccessLocked(container);

    initPopover(keepUserSignedInPopover, container, POPOVER_CLICK_BOTTOM, {
        content: keepUserSignedInContainer
    });

    initPopover(keepUserSignedInPopoverCheckoutPage, container, {
        content: keepUserSignedInContainer
    });

    initPopover(pdpChatPopover, container, POPOVER_CLICK_BOTTOM, {
        html: true,
        container: pdpChatWrapper,
        placement: 'bottom',
        content: pdpChatContainer
    });

    initPopoverClose(container);
    initPopoverCloseSelector(container);
};

/**
 * Attach search related events
 */
function attachSearchEvents() {
    const body = $(document.body);

    body.on('search:updateProducts', () => {
        initPopoverAllLocal();
    });

    body.on('search:showMore', () => {
        initPopoverAllLocal();
    });

    document.addEventListener('slot-loaded', e => {
        const { target } = e;
        initPopoverAllLocal(target);
    });

    body.on('sheet:injectForm', () => {
        initPopoverAllLocal();
    });
}

/**
 * All of usual popover elements on site
 * TODO: Ideally refactor to something dynamical, when it appear in DOM - initialize it
 */
const initPopoverAll = () => {
    attachSearchEvents();
    initPopoverAllLocal();
};

module.exports.POPOVER_HOVER_BOTTOM = POPOVER_HOVER_BOTTOM;
// export the initialization function for places where popover may be initialized dynamically
module.exports.initPopoverClose = initPopoverClose;
module.exports.initPopoverCloseSelector = initPopoverCloseSelector;
// export function for single initialization of popover
module.exports.initPopover = initPopover;
module.exports.initPopoverPromotions = initPopoverPromotions;
module.exports.initPopoverEarlyAccessLocked = initPopoverEarlyAccessLocked;
module.exports.updateEarlyAccessLockTooltip = updateEarlyAccessLockTooltip;
module.exports.initPopoverAll = initPopoverAll;
module.exports.initPopoverWishList = initPopoverWishList;
